import {
	AccessTime as AccessTimeIcon,
	Cancel as CancelIcon,
	CarCrash as CarCrashIcon,
	CheckCircle as CheckCircleIcon,
	CheckCircleOutline,
	ChevronLeft as ChevronLeftIcon,
	ChevronRight as ChevronRightIcon,
	Crop as CropIcon,
	FullscreenExit as FullscreenExitIcon,
	HighlightOff as HighlightOffIcon,
	History as HistoryIcon,
	Lock as LockIcon,
	RestartAlt as RestartAltIcon,
	ReceiptLong as ReceiptLongIcon
} from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { Box, Button, CircularProgress, Grid2, IconButton, TextField, Tooltip, Typography } from "@mui/material";
import moment from "moment";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";

import { IVehicleLogRowDetails, IVehicleLogViolationDetails } from ".";
import { ReactComponent as NoImageIcon } from "../../assets/icons/image-not-found-icon.svg";
import EvidenceImage from "../../components/EvidenceImage";
import { useNotification } from "../../components/FeedbackNotification/useNotification";
import PlateImage from "../../components/PlateImage";
import { useAppSelector } from "../../redux";
import {
	useLazyGetVehicleDetailsQuery,
	useUpdateEvidenceImageMutation,
	useUpdateVehicleEventMutation,
	useUploadPlateImageMutation,
	useValidateViolationsMutation
} from "../../redux/reducers/alerts.reducer";
import {
	useGetVehicleCategoriesQuery,
	useGetVehicleTypesListQuery,
	useGetViolationsListQuery
} from "../../redux/reducers/settings.reducer";
import {
	EVENT_PRODUCT_TYPE_E,
	IEvidenceImageURLDetails,
	IGetAlertEventsResponseData,
	IUpdateVehicleEventRequestData,
	IViolationDetails
} from "../../types";
import { convertSnakeToTitleCase } from "../../utils/commonUtils";
import {
	BBOX_STROKE_COLOR,
	BBOX_STROKE_WIDTH,
	NON_HSRP_VIOLATION_NAMES,
	VALIDATION_REGEX
} from "../../utils/constants";
import CropDialog from "./CropDialog";
import GenerateChallanDialog from "./GenerateChallanDialog";
import PreviousOffencesDialog from "./PreviousOffencesDialog";
import VahanDataDialog from "./VahanDataDialog";
import { ICoordinate, IDimensions } from "../../hooks/useCreateImageURL";

interface IVehicleLogContentProps {
	vehicleLogDetails: IVehicleLogRowDetails | null;
	previousButtonDisabled?: boolean;
	nextButtonDisabled?: boolean;
	onUpdated: (eventDetails: IGetAlertEventsResponseData) => void;
	onChallanGenerated: (eventId: number, challanGenerated: boolean, retryChallan: boolean) => void;
	onPrevious?: (vehicleLogId: number) => void;
	onNext?: (vehicleLogId: number) => void;
}

export interface IFullscreenImageProps {
	src: string;
	bbox: [number, number, number, number][];
	imageDimensions: IDimensions;
}

interface IEvidenceImageSelectionProps {
	index: number;
	selected: boolean;
	src: string;
	handleEvidenceImageClick: () => void;
	readOnly?: boolean;
	lockDetails?: ICropSourceLockDetails;
}

interface ICropSourceLockDetails {
	forPlate: boolean;
	forViolation: boolean;
}

const MAX_EVIDENCE_IMAGES = 2;

// The image a crop was taken from is identified by its file name. Falls back to the frame image when
// no file name is saved or the saved one is no longer part of the evidence images.
function findCropSourceImage(
	images: IEvidenceImageURLDetails[] | undefined,
	fileName?: string
): IEvidenceImageURLDetails | undefined {
	if (!images) return undefined;

	return (
		(fileName ? images.find((item) => item.file_name === fileName) : undefined) ??
		images.find((item) => item.evidence_type === "FRAME")
	);
}

const FullscreenImage: FC<IFullscreenImageProps> = (props) => {
	const { src, bbox, imageDimensions } = props;

	const canvasRef = useRef<HTMLCanvasElement>(null);

	const [canvasDimensions, setCanvasDimensions] = useState<IDimensions>({ height: 0, width: 0 });

	const getScaledCoordinate = useCallback(
		(coordinate: ICoordinate): ICoordinate => {
			const { height: imageHeight, width: imageWidth } = imageDimensions;
			const { height: canvasHeight, width: canvasWidth } = canvasDimensions;

			return {
				x: (canvasWidth * coordinate.x) / imageWidth,
				y: (canvasHeight * coordinate.y) / imageHeight
			};
		},
		[canvasDimensions, imageDimensions]
	);

	useEffect(() => {
		if (src && canvasRef.current && canvasRef.current.offsetWidth) {
			setCanvasDimensions((currentDimensions) => ({
				...currentDimensions,
				width: canvasRef.current?.offsetWidth ?? 0
			}));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [src, canvasRef.current]);

	useEffect(() => {
		if (canvasDimensions.width) {
			const canvasHeight = (canvasDimensions.width * imageDimensions.height) / imageDimensions.width;

			setCanvasDimensions((currentDimensions) => ({
				...currentDimensions,
				height: canvasHeight
			}));
		}
	}, [canvasDimensions.width, imageDimensions, src]);

	useEffect(() => {
		if (canvasRef.current && src) {
			// eslint-disable-next-line no-self-assign
			canvasRef.current.width = canvasRef.current.width;

			const canvasContext = canvasRef.current.getContext("2d");
			if (!canvasContext) return;

			canvasContext.strokeStyle = BBOX_STROKE_COLOR;
			canvasContext.lineWidth = BBOX_STROKE_WIDTH;

			for (const bboxItem of bbox) {
				const startCoordinate = getScaledCoordinate({ x: bboxItem[0], y: bboxItem[1] });
				const bboxDimensions = getScaledCoordinate({ x: bboxItem[2], y: bboxItem[3] });

				canvasContext.strokeRect(startCoordinate.x, startCoordinate.y, bboxDimensions.x, bboxDimensions.y);
			}
		}
	}, [bbox, getScaledCoordinate, src]);

	return (
		<Box className="evidence-image-wrapper">
			<Box sx={{ width: "100%", height: canvasDimensions.height, position: "relative" }}>
				<canvas
					ref={canvasRef}
					width={canvasDimensions.width}
					height={canvasDimensions.height}
					style={{
						width: "100%",
						backgroundSize: "contain",
						backgroundPosition: "center",
						backgroundRepeat: "no-repeat",
						backgroundImage: `url(${src})`
					}}
				/>
			</Box>
		</Box>
	);
};

const EvidenceImageSelection: FC<IEvidenceImageSelectionProps> = (props) => {
	const { index, selected, src, handleEvidenceImageClick, readOnly, lockDetails } = props;

	const [showError, setShowError] = useState<boolean>(false);

	useEffect(() => {
		if (src) {
			const image = new Image();
			image.onload = () => setShowError(false);
			image.onerror = () => setShowError(true);
			image.src = src;
		}
	}, [src]);

	const lockTooltip =
		!lockDetails || readOnly
			? ""
			: lockDetails.forPlate && lockDetails.forViolation
			? "Used for the plate and violation crop, cannot be de-selected"
			: lockDetails.forPlate
			? "Used for the plate crop, cannot be de-selected"
			: "Used for the violation crop, cannot be de-selected";

	return (
		<Tooltip arrow placement="top" title={lockTooltip}>
			<Box
				key={index}
				className={`evidence-image-selection ${selected ? "selected" : ""} ${lockDetails ? "locked" : ""}`}
				onClick={!readOnly ? handleEvidenceImageClick : undefined}
				sx={readOnly ? { cursor: "default" } : {}}
			>
				{showError ? (
					<Box
						sx={{
							width: "100%",
							height: "100%",
							display: "flex",
							flexDirection: "column",
							justifyContent: "center",
							alignItems: "center"
						}}
					>
						<NoImageIcon width={34} fill="#d5d5d5" stroke="#d5d5d5" />
						<Typography variant="body2" color="#999999" fontWeight={500}>
							Image not available
						</Typography>
					</Box>
				) : (
					<img src={src} alt={`evidence-${index}`} />
				)}

				{selected && (
					<Box className="evidence-image-selection-icon">
						{lockDetails ? (
							<LockIcon sx={{ color: "white", fontSize: 12 }} />
						) : (
							<span style={{ color: "white", fontSize: 14 }}>✓</span>
						)}
					</Box>
				)}
			</Box>
		</Tooltip>
	);
};

const VehicleLogContent: FC<IVehicleLogContentProps> = (props) => {
	const {
		vehicleLogDetails,
		previousButtonDisabled,
		nextButtonDisabled,
		onPrevious,
		onNext,
		onUpdated,
		onChallanGenerated
	} = props;

	const authState = useAppSelector((state) => state.auth);

	const { notify } = useNotification();

	const [eventViolations, setEventViolations] = useState<IVehicleLogViolationDetails[]>([]);
	const [selectedImageIndex, setSelectedImageIndex] = useState<number>(0);
	const [fullscreenImageProps, setFullscreenImageProps] = useState<IFullscreenImageProps | null>(null);
	const [cropType, setCropType] = useState<"plate" | "violation">("plate");
	const [showCropDialog, setShowCropDialog] = useState<boolean>(false);
	const [croppedViolationImage, setCroppedViolationImage] = useState<string | null>(null);
	const [selectedEvidenceImages, setSelectedEvidenceImages] = useState<number[]>([]);
	const [violationImageError, setViolationImageError] = useState<boolean>(false);
	const [validateButtonLoading, setValidateButtonLoading] = useState<boolean>(false);
	const [rejectButtonLoading, setRejectButtonLoading] = useState<boolean>(false);
	const [showVahanDataDialog, setShowVahanDataDialog] = useState<boolean>(false);
	// const [showSarathiDataDialog, setShowSarathiDataDialog] = useState<boolean>(false);
	const [showPreviousOffencesDialog, setShowPreviousOffencesDialog] = useState<boolean>(false);
	const [showChallanGenerationDialog, setShowChallanGenerationDialog] = useState<boolean>(false);
	const [cropDialogSourceImage, setCropDialogSourceImage] = useState<{ url: string; fileName?: string } | null>(null);

	const {
		reset,
		watch,
		setValue,
		register,
		setError,
		clearErrors,
		handleSubmit,
		formState: { errors }
	} = useForm<IVehicleLogRowDetails>({ reValidateMode: "onChange" });

	const vehicleCategory = watch("plate_type");
	const vehicleType = watch("vehicle_type");
	const plateImageCrop = watch("crop_coordinates.plate");
	const violationImageCrop = watch("crop_coordinates.violation");
	const plateImageName = watch("crop_coordinates.plate_image_name");
	const violationImageName = watch("crop_coordinates.violation_image_name");
	const vehicleNumber = watch("vehicle_number");

	// UPDATE VEHICLE EVENT
	const [updateVehicleEvent, { reset: resetUpdateVehicleEventState }] = useUpdateVehicleEventMutation();

	// VALIDATE VIOLATIONS
	const [validateViolations] = useValidateViolationsMutation();

	// UPDATE EVIDENCE IMAGE
	const [updateEvidenceImage] = useUpdateEvidenceImageMutation();

	// UPLOAD PLATE IMAGE
	const [uploadPlateImage] = useUploadPlateImageMutation();

	// GET VEHICLE CATEGORIES
	const { data: vehicleCategoriesList } = useGetVehicleCategoriesQuery();

	// GET VEHICLE TYPES LIST
	const { data: vehicleTypesList } = useGetVehicleTypesListQuery(undefined, {
		skip: !authState.user.token || authState.user.token === "invalid"
	});

	// GET VIOLATIONS LIST
	const { data: getViolationsListResponse } = useGetViolationsListQuery();

	// GET VEHICLE DETAILS
	// const {
	// 	data: vehicleDetailsOld,
	// 	isLoading: getVehicleDetailsLoadingOld,
	// 	isFetching: getVehicleDetailsFetchingOld
	// } = useGetVehicleDetailsQuery(
	// 	{ number_plate: (vehicleNumber || "").toUpperCase() },
	// 	{ skip: !vehicleNumber || !VALIDATION_REGEX.NUMBER_PLATE.test(vehicleNumber) }
	// );

	const [
		getVehicleDetails,
		{
			data: vehicleDetails,
			isLoading: getVehicleDetailsLoading,
			isFetching: getVehicleDetailsFetching,
			originalArgs: getVehicleDetailsArgs
		}
	] = useLazyGetVehicleDetailsQuery();

	const violationsList = useMemo<IViolationDetails[]>(() => {
		if (!getViolationsListResponse) return [];
		return getViolationsListResponse.filter(
			(violation) =>
				violation.product_type === EVENT_PRODUCT_TYPE_E.RECON && violation.supported_vehicle_types.includes(vehicleType)
		);
	}, [getViolationsListResponse, vehicleType]);

	const nonHsrpViolation = useMemo<IViolationDetails | undefined>(
		() => violationsList.find((violation) => NON_HSRP_VIOLATION_NAMES.includes(violation.name)),
		[violationsList]
	);

	// Same order as EvidenceImage: FRAME first, then evidence images
	const orderedImages = useMemo(() => {
		if (!vehicleLogDetails?.vehicle_images?.length) return [];
		const frameImages: typeof vehicleLogDetails.vehicle_images = [];
		const evidenceImages: typeof vehicleLogDetails.vehicle_images = [];
		for (const item of vehicleLogDetails.vehicle_images) {
			if (item.evidence_type === "FRAME") frameImages.push(item);
			else evidenceImages.push(item);
		}
		return [...frameImages, ...evidenceImages];
	}, [vehicleLogDetails]);

	const readOnlyPermission = useMemo(
		() =>
			!authState.user.permissions ||
			!authState.user.permissions.violation_logs.edit ||
			!vehicleLogDetails ||
			vehicleLogDetails.status !== "pending",
		[authState.user.permissions, vehicleLogDetails]
	);

	// Evidence images that were used as the source for the plate / violation crop. These have to stay
	// selected as evidence, so they are force-selected and cannot be de-selected by the user.
	const lockedEvidenceImages = useMemo(() => {
		const lockedImages = new Map<number, ICropSourceLockDetails>();
		if (!vehicleLogDetails) return lockedImages;

		for (const imageDetails of vehicleLogDetails.vehicle_images) {
			if (!imageDetails.file_name) continue;

			const forPlate = imageDetails.file_name === plateImageName;
			const forViolation = imageDetails.file_name === violationImageName;

			if (forPlate || forViolation) lockedImages.set(imageDetails.id, { forPlate, forViolation });
		}

		return lockedImages;
	}, [vehicleLogDetails, plateImageName, violationImageName]);

	// A crop source image can only be locked if it is still part of the evidence images
	const cropSourceImageMissing = useMemo<boolean>(() => {
		if (!vehicleLogDetails) return false;

		const availableFileNames = new Set(
			vehicleLogDetails.vehicle_images.map((imageDetails) => imageDetails.file_name).filter(Boolean)
		);

		return (
			(!!plateImageName && !availableFileNames.has(plateImageName)) ||
			(!!violationImageName && !availableFileNames.has(violationImageName))
		);
	}, [vehicleLogDetails, plateImageName, violationImageName]);

	const validateButtonError = useMemo<string | null>(() => {
		if (eventViolations.length <= 0) return "Select at least one validation type to validate";
		if (selectedEvidenceImages.length < MAX_EVIDENCE_IMAGES) return "Select two images as evidence to validate";
		if (
			cropSourceImageMissing ||
			!Array.from(lockedEvidenceImages.keys()).every((imageId) => selectedEvidenceImages.includes(imageId))
		)
			return "The image used to adjust the plate/violation crop must be selected as evidence";
		if (!VALIDATION_REGEX.NUMBER_PLATE.test(vehicleNumber)) return "Invalid plate number";
		return null;
	}, [cropSourceImageMissing, eventViolations.length, lockedEvidenceImages, selectedEvidenceImages, vehicleNumber]);

	function handleChangeSelectedImageIndex(value: number) {
		setSelectedImageIndex(value);
	}

	function handleShowFullscreenImage(props: IFullscreenImageProps) {
		setFullscreenImageProps(props);
	}

	function handleHideFullscreenImage() {
		setFullscreenImageProps(null);
	}

	function openChallanGenerationDialog() {
		setShowChallanGenerationDialog(true);
	}

	function closeChallanGenerationDialog(
		success: boolean,
		challanGenerated: boolean = false,
		retryChallan: boolean = false
	) {
		setShowChallanGenerationDialog(false);
		if (success) onChallanGenerated(vehicleLogDetails?.id ?? 0, challanGenerated, retryChallan);
	}

	async function handleSaveButtonClick(formData: IVehicleLogRowDetails, isRejected: boolean = false) {
		if (vehicleLogDetails) {
			if (isRejected) {
				if (!formData.remarks?.trim()) {
					setError("remarks", { type: "required", message: "Remarks are required to reject" }, { shouldFocus: true });
					return;
				}
				setRejectButtonLoading(true);
			} else {
				if (eventViolations.length <= 0) {
					notify("Select at least one violation type to validate", "error");
					return;
				}

				if (selectedEvidenceImages.length < MAX_EVIDENCE_IMAGES) {
					notify("Select two images as evidence to validate", "error");
					return;
				}

				if (
					cropSourceImageMissing ||
					!Array.from(lockedEvidenceImages.keys()).every((imageId) => selectedEvidenceImages.includes(imageId))
				) {
					notify("The image used to adjust the plate/violation crop must be selected as evidence", "error");
					return;
				}

				setValidateButtonLoading(true);
			}

			try {
				await validateViolations({
					event_id: vehicleLogDetails.id,
					violations: eventViolations.map((violationItem) => ({
						lane_id: violationItem.lane_id || null,
						violation_id: violationItem.violation_id
					}))
				});

				for (const evidenceImage of vehicleLogDetails.vehicle_images) {
					const isSelected = selectedEvidenceImages.includes(evidenceImage.id);
					if (evidenceImage.ignore === !isSelected) continue;

					await updateEvidenceImage({ id: evidenceImage.id, data: { ignore: !isSelected } }).unwrap();
				}

				const plateSourceImage = findCropSourceImage(
					vehicleLogDetails?.vehicle_images,
					formData.crop_coordinates?.plate_image_name
				);

				// Create cropped plate image base64
				if (plateSourceImage && formData.crop_coordinates?.plate && formData.crop_coordinates.plate.length === 4) {
					const croppedPlateImageBase64 = await new Promise<string>((resolve, reject) => {
						const canvas = document.createElement("canvas");
						const ctx = canvas.getContext("2d");
						if (!ctx) {
							reject(new Error("Could not get canvas context"));
							return;
						}

						const img = new Image();
						img.crossOrigin = "anonymous";
						img.onload = () => {
							const [x, y, width, height] = formData.crop_coordinates.plate;

							canvas.width = width;
							canvas.height = height;

							ctx.drawImage(img, x, y, width, height, 0, 0, width, height);
							resolve(canvas.toDataURL("image/jpeg"));
						};
						img.onerror = () => {
							reject(new Error("Failed to load image"));
						};
						img.src = plateSourceImage.url;
					});

					await uploadPlateImage({ event_id: vehicleLogDetails.id, image: croppedPlateImageBase64 }).unwrap();
				}

				const requestData: IUpdateVehicleEventRequestData = {
					unique_id: vehicleLogDetails.event_id,
					data: {
						validated_status: isRejected ? "reject" : "approved"
					}
				};

				if (formData.vehicle_number.toLowerCase() !== vehicleLogDetails.vehicle_number.toLowerCase()) {
					requestData.data.number_plate = formData.vehicle_number.toUpperCase();
				}

				if (formData.vehicle_type) requestData.data.vehicle_type = Number(formData.vehicle_type);
				if (formData.category_id) requestData.data.vehicle_category = Number(formData.category_id);
				if (formData.speed) requestData.data.speed = Number(formData.speed);
				if (formData.radar_speed) requestData.data.radar_speed = Number(formData.radar_speed);

				// if (formData.vehicle_class_id) requestData.data.vehicle_class = Number(formData.vehicle_class_id);
				// if (formData.brand_id) requestData.data.vehicle_brand = Number(formData.brand_id);
				// if (formData.model_id) requestData.data.vehicle_model = Number(formData.model_id);
				// if (formData.color_id) requestData.data.color = Number(formData.color_id);
				// if (formData.direction) requestData.data.direction = formData.direction;

				if (formData.crop_coordinates) {
					requestData.data.crop_co_ordinates = {
						plate: formData.crop_coordinates.plate ?? [],
						violation: [formData.crop_coordinates.violation],
						...(formData.crop_coordinates.plate_image_name != null && {
							plate_image_name: formData.crop_coordinates.plate_image_name
						}),
						...(formData.crop_coordinates.violation_image_name != null && {
							violation_image_name: formData.crop_coordinates.violation_image_name
						})
					};
				}

				if (formData.remarks) {
					requestData.data.comment = formData.remarks;
				}

				const updateVehicleEventResponse = await updateVehicleEvent(requestData).unwrap();

				if (!isRejected) openChallanGenerationDialog();

				onUpdated({
					...updateVehicleEventResponse,
					violations: eventViolations.map((violationItem) => ({
						id: violationItem.id,
						violation_name: violationItem.name,
						lane: violationItem.lane_id,
						lane_name: violationItem.lane,
						violation: violationItem.violation_id
					})),
					new_evidence_images: vehicleLogDetails.vehicle_images.map((evidenceImage) => ({
						...evidenceImage,
						ignore: !selectedEvidenceImages.includes(evidenceImage.id)
					}))
				});
				resetUpdateVehicleEventState();

				if (isRejected) setRejectButtonLoading(false);
				else setValidateButtonLoading(false);
			} catch (error: any) {
				notify("data" in error && error.data ? String(error.data) : "Something went wrong", "error");
				if (isRejected) setRejectButtonLoading(false);
				else setValidateButtonLoading(false);
			}
		}
	}

	function handleResetData() {
		if (vehicleLogDetails) {
			setEventViolations(vehicleLogDetails.violations ?? []);
			reset({
				...vehicleLogDetails,
				speed: vehicleLogDetails.speed.replace("km/hr", "").trim(),
				radar_speed: vehicleLogDetails.radar_speed.replace("km/hr", "").trim()
			});
			setSelectedImageIndex(0);
			setSelectedEvidenceImages(
				vehicleLogDetails.vehicle_images
					.filter((item) => !item.ignore)
					.slice(0, MAX_EVIDENCE_IMAGES)
					.map((item) => item.id)
			);
			setViolationImageError(false);
			setCroppedViolationImage(null);
			setCropDialogSourceImage(null);
			setShowCropDialog(false);
		}
	}

	function handleChangeVehicleCategory(value: "HSRP" | "NON_HSRP") {
		const categoryId = vehicleCategoriesList?.find((item) => item.name === value)?.id ?? 0;
		setValue("category_id", categoryId);
		setValue("plate_type", value);

		// Keep the Non HSRP violation selection in sync with the plate type
		if (!nonHsrpViolation) return;

		setEventViolations((currentViolations) => {
			const isSelected = currentViolations.some((v) => v.violation_id === nonHsrpViolation.id);

			if (value === "NON_HSRP") {
				if (isSelected) return currentViolations;

				return [
					...currentViolations,
					{
						id: nonHsrpViolation.id,
						violation_id: nonHsrpViolation.id,
						name: nonHsrpViolation.name,
						is_speed_related: false,
						lane_id: vehicleLogDetails?.lanes?.[0]?.id ?? 0,
						lane: vehicleLogDetails?.lanes?.[0]?.name ?? ""
					}
				];
			}

			if (!isSelected) return currentViolations;
			return currentViolations.filter((v) => v.violation_id !== nonHsrpViolation.id);
		});
	}

	function handleEvidenceImageClick(imageUrl: number) {
		// The crop source images are locked and cannot be de-selected
		if (lockedEvidenceImages.has(imageUrl)) return;

		setSelectedEvidenceImages((currentSelected) => {
			if (currentSelected.includes(imageUrl)) {
				return currentSelected.filter((url) => url !== imageUrl);
			} else if (currentSelected.length < MAX_EVIDENCE_IMAGES) {
				return [...currentSelected, imageUrl];
			}
			return currentSelected;
		});
	}

	function handleChangeVehicleType(value: number) {
		setValue("vehicle_type", value);
	}

	function handleViolationClick(violation: IVehicleLogViolationDetails) {
		// Keep the plate type in sync when the Non HSRP violation is toggled
		if (NON_HSRP_VIOLATION_NAMES.includes(violation.name)) {
			const isSelected = eventViolations.some((v) => v.violation_id === violation.violation_id);
			const plateType = isSelected ? "HSRP" : "NON_HSRP";
			const categoryId = vehicleCategoriesList?.find((item) => item.name === plateType)?.id ?? 0;

			setValue("category_id", categoryId);
			setValue("plate_type", plateType);
		}

		setEventViolations((currentViolations) => {
			const isSelected = currentViolations.some((v) => v.violation_id === violation.violation_id);
			if (isSelected) {
				return currentViolations.filter((v) => v.violation_id !== violation.violation_id);
			} else {
				// const selectedLane = lanesList[0];
				return [
					...currentViolations,
					{
						...violation,
						is_speed_related: violation.name.toLowerCase().includes("speed"),
						lane_id: vehicleLogDetails?.lanes?.[0]?.id ?? 0,
						lane_name: vehicleLogDetails?.lanes?.[0]?.name ?? ""
					}
				];
			}
		});
	}

	function handleOpenCropDialog(type: "plate" | "violation") {
		const frameImage = orderedImages.find((item) => item.evidence_type === "FRAME");
		const currentImage = orderedImages[selectedImageIndex] ?? frameImage ?? orderedImages[0];
		setCropDialogSourceImage(currentImage ? { url: currentImage.url, fileName: currentImage.file_name } : null);
		setCropType(type);
		setShowCropDialog(true);
	}

	function handleCloseCropDialog() {
		setCropDialogSourceImage(null);
		setShowCropDialog(false);
	}

	function handleCropComplete(coordinates: [number, number, number, number]) {
		if (!vehicleLogDetails) return;

		if (cropType === "plate") {
			setValue("crop_coordinates.plate", coordinates);
			setValue("crop_coordinates.plate_image_name", cropDialogSourceImage?.fileName);
		} else {
			setValue("crop_coordinates.violation", coordinates);
			setValue("crop_coordinates.violation_image_name", cropDialogSourceImage?.fileName);
			setCroppedViolationImage(null);
		}
	}

	function handleOpenVahanDataDialog() {
		if (
			!vehicleDetails ||
			vehicleDetails.api_status === "failed" ||
			getVehicleDetailsArgs?.number_plate?.toLowerCase() !== vehicleNumber.toLowerCase()
		) {
			getVehicleDetails({ number_plate: vehicleNumber.toUpperCase() })
				.unwrap()
				.then(() => setShowVahanDataDialog(true))
				.catch(() => {});
		} else {
			setShowVahanDataDialog(true);
		}
	}

	function handleCloseVahanDataDialog() {
		setShowVahanDataDialog(false);
	}

	function forceRefetchVehicleDetails() {
		getVehicleDetails({ number_plate: vehicleNumber.toUpperCase(), force_refresh: true });
	}

	// function handleOpenSarathiDataDialog() {
	// 	if (!vehicleDetails || getVehicleDetailsArgs?.number_plate?.toLowerCase() !== vehicleNumber.toLowerCase()) {
	// 		getVehicleDetails({ number_plate: vehicleNumber.toUpperCase() })
	// 			.unwrap()
	// 			.then(() => setShowSarathiDataDialog(true))
	// 			.catch(() => {});
	// 	} else {
	// 		setShowSarathiDataDialog(true);
	// 	}
	// }

	// function handleCloseSarathiDataDialog() {
	// 	setShowSarathiDataDialog(false);
	// }

	function handleOpenPreviousOffencesDialog() {
		setShowPreviousOffencesDialog(true);
	}

	function handleClosePreviousOffencesDialog() {
		setShowPreviousOffencesDialog(false);
	}

	const keypressEventListener = useCallback((event: KeyboardEvent) => {
		if (event.key === "Escape") {
			handleHideFullscreenImage();
		}
	}, []);

	useEffect(() => {
		document.addEventListener("keydown", keypressEventListener, false);
		return () => {
			document.removeEventListener("keydown", keypressEventListener, false);
		};
	}, [keypressEventListener]);

	useEffect(() => {
		if (vehicleLogDetails) {
			setEventViolations(vehicleLogDetails.violations ?? []);
			reset({
				...vehicleLogDetails,
				speed: vehicleLogDetails.speed.replace("km/hr", "").trim(),
				radar_speed: vehicleLogDetails.radar_speed.replace("km/hr", "").trim(),
				vehicle_type: vehicleLogDetails.vehicle_type
			});
			setSelectedImageIndex(0);
			setSelectedEvidenceImages(
				vehicleLogDetails.vehicle_images
					?.filter((item) => !item.ignore)
					.slice(0, MAX_EVIDENCE_IMAGES)
					.map((item) => item.id)
			);
			setViolationImageError(!vehicleLogDetails.violation_image);
			setCroppedViolationImage(null);
			setCropDialogSourceImage(null);
			setShowCropDialog(false);

			if (vehicleLogDetails.vehicle_number && VALIDATION_REGEX.NUMBER_PLATE.test(vehicleLogDetails.vehicle_number)) {
				getVehicleDetails({ number_plate: vehicleLogDetails.vehicle_number });
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [vehicleLogDetails]);

	// Keep the crop source image(s) selected as evidence, evicting the oldest unlocked selection when
	// the locked images do not fit within the selection limit
	useEffect(() => {
		if (lockedEvidenceImages.size <= 0) return;

		const lockedImageIds = Array.from(lockedEvidenceImages.keys());

		setSelectedEvidenceImages((currentSelected) => {
			if (lockedImageIds.every((imageId) => currentSelected.includes(imageId))) return currentSelected;

			const unlockedSelected = currentSelected.filter((imageId) => !lockedEvidenceImages.has(imageId));
			const freeSlots = MAX_EVIDENCE_IMAGES - lockedImageIds.length;

			return [...lockedImageIds, ...(freeSlots > 0 ? unlockedSelected.slice(-freeSlots) : [])];
		});
		// `selectedEvidenceImages` is a dependency so the lock is re-applied after a reset, the updater
		// returns the current selection unchanged once it is satisfied, so this cannot loop
	}, [lockedEvidenceImages, selectedEvidenceImages]);

	useEffect(() => {
		if (violationImageError && violationImageCrop) {
			const violationSourceImage = findCropSourceImage(vehicleLogDetails?.vehicle_images, violationImageName);
			if (!violationSourceImage) return;

			const canvas = document.createElement("canvas");
			const ctx = canvas.getContext("2d");
			if (!ctx) return;

			const img = new Image();
			img.crossOrigin = "anonymous";
			img.onload = () => {
				const [x, y, width, height] = violationImageCrop;

				canvas.width = width;
				canvas.height = height;

				ctx.drawImage(img, x, y, width, height, 0, 0, width, height);

				setCroppedViolationImage(canvas.toDataURL("image/jpeg"));
			};
			img.src = violationSourceImage.url;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [violationImageError, violationImageCrop, violationImageName]);

	return (
		<>
			<Grid2 container spacing={2}>
				{onPrevious && onNext ? (
					<Grid2 size={{ xs: 12 }}>
						<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
							<Typography variant="h6">EVIDENCE VALIDATION - QUICK TOUCH INTERFACE</Typography>

							<Box>
								<Button
									size="small"
									variant="outlined"
									sx={{ mr: 1 }}
									disabled={previousButtonDisabled}
									startIcon={<ChevronLeftIcon />}
									onClick={() => onPrevious(vehicleLogDetails?.id ?? 0)}
								>
									Previous
								</Button>

								<Button
									size="small"
									variant="outlined"
									sx={{ ml: 1 }}
									disabled={nextButtonDisabled}
									endIcon={<ChevronRightIcon />}
									onClick={() => onNext(vehicleLogDetails?.id ?? 0)}
								>
									Next
								</Button>
							</Box>
						</Box>
					</Grid2>
				) : null}

				<Grid2 size={{ xs: 12 }}>
					<Box className="event-log-details-card">
						<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-around" }}>
							<Box>
								<Typography>Officer : {vehicleLogDetails?.officer}</Typography>
							</Box>
							<Box>
								<Typography>District : {vehicleLogDetails?.location_name}</Typography>
							</Box>
							<Box>
								<Typography>
									Timestamp :{" "}
									{vehicleLogDetails ? moment(vehicleLogDetails.timestamp).format("DD/MM/YYYY hh:mm:ss A") : ""}
								</Typography>
							</Box>
							<Box>
								<Typography sx={{ display: "flex", alignItems: "center", gap: "0.25rem" }}>
									Status:
									<span
										style={{ width: "unset" }}
										className={`log-status-indicator ${
											vehicleLogDetails?.status === "approved"
												? vehicleLogDetails.challan_status === "failed"
													? "rejected"
													: "verified"
												: vehicleLogDetails?.status === "reject"
												? "rejected"
												: "pending"
										}`}
									>
										{vehicleLogDetails?.status === "approved" ? (
											vehicleLogDetails.challan_status === "failed" ? (
												<HighlightOffIcon sx={{ fontSize: "18px" }} />
											) : (
												<CheckCircleOutline sx={{ fontSize: "18px" }} />
											)
										) : vehicleLogDetails?.status === "reject" ? (
											<HighlightOffIcon sx={{ fontSize: "18px" }} />
										) : (
											<AccessTimeIcon sx={{ fontSize: "18px" }} />
										)}

										{vehicleLogDetails?.status === "approved"
											? vehicleLogDetails?.challan_status === "failed"
												? "Challan Failed"
												: vehicleLogDetails.challan_status === "issued"
												? "Challan Issued"
												: "Verified"
											: vehicleLogDetails?.status === "reject"
											? "Rejected"
											: "Pending"}
									</span>
								</Typography>
							</Box>
						</Box>
					</Box>
				</Grid2>

				{/* Left Column */}
				<Grid2 size={{ xs: 12, md: 8 }}>
					{/* Main Image Carousel */}
					<Box className="event-log-details-card" sx={{ marginBottom: "1rem" }}>
						<EvidenceImage
							bbox={vehicleLogDetails?.bbox ?? []}
							vehicleLogDetails={vehicleLogDetails}
							violations={eventViolations}
							activeImage={selectedImageIndex}
							onOpenFullscreenImage={handleShowFullscreenImage}
							onChangeActiveImage={handleChangeSelectedImageIndex}
						/>
					</Box>

					{/* Plate Crop and Plate No. */}
					<Box className="event-log-details-card" sx={{ marginBottom: "1rem" }}>
						<Grid2 container spacing={2} alignItems="center">
							<Grid2 size={{ xs: 12, md: 6 }}>
								<Box sx={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
									<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
										<Typography variant="body2" color="#0C0C0D">
											Plate Crop
										</Typography>

										{!readOnlyPermission && (
											<Button
												variant="outlined"
												color="secondary"
												size="small"
												startIcon={<CropIcon />}
												onClick={() => handleOpenCropDialog("plate")}
											>
												Adjust Crop
											</Button>
										)}
									</Box>

									<PlateImage
										eventId={vehicleLogDetails?.plate_image_uuid ?? ""}
										plateCrop={plateImageCrop}
										plateImageName={plateImageName}
										sourceImages={vehicleLogDetails?.vehicle_images}
									/>
								</Box>
							</Grid2>

							<Grid2 size={{ xs: 12, md: 6 }}>
								<Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem", alignItems: "flex-start" }}>
									<TextField
										fullWidth
										variant="outlined"
										color="primary"
										size="small"
										label="Plate Number"
										autoComplete="off"
										error={!!errors.vehicle_number}
										helperText={
											errors.vehicle_number && errors.vehicle_number.message
												? errors.vehicle_number.message.toString()
												: ""
										}
										slotProps={{
											input: {
												classes: { root: "vehicle-number-input-root" },
												readOnly: readOnlyPermission
											}
										}}
										onFocus={(event) => {
											if (event.target.value.toLowerCase().startsWith("unreadable")) {
												event.target.select();
											}
										}}
										{...register("vehicle_number", {
											required: "Vehicle number is required",
											validate: (value) => {
												if (
													vehicleLogDetails &&
													value !== vehicleLogDetails.vehicle_number &&
													!VALIDATION_REGEX.NUMBER_PLATE.test(value)
												) {
													return "Enter a valid vehicle number";
												}

												return true;
											}
										})}
									/>

									<Box sx={{ display: "flex", alignItems: "center", gap: "0.5rem", width: "100%" }}>
										<Button
											disableElevation
											variant={vehicleCategory === "HSRP" ? "contained" : "outlined"}
											size="small"
											sx={{ flex: 1, ...(readOnlyPermission ? { cursor: "default" } : {}) }}
											onClick={() => !readOnlyPermission && handleChangeVehicleCategory("HSRP")}
										>
											HSRP
										</Button>
										<Button
											disableElevation
											variant={vehicleCategory === "NON_HSRP" ? "contained" : "outlined"}
											size="small"
											sx={{ flex: 1, ...(readOnlyPermission ? { cursor: "default" } : {}) }}
											onClick={() => !readOnlyPermission && handleChangeVehicleCategory("NON_HSRP")}
										>
											NON-HSRP
										</Button>
									</Box>
								</Box>
							</Grid2>
						</Grid2>
					</Box>

					{/* Select Images as Evidence */}
					<Box className="event-log-details-card" sx={{ marginBottom: "1rem" }}>
						<Typography fontWeight={600} mb={1}>
							Select 2 images as evidence{" "}
							<span style={{ fontWeight: 400, fontSize: 13, color: "#888" }}>
								{selectedEvidenceImages?.length}/2 selected
							</span>
						</Typography>

						<Box className="evidence-image-selection-wrapper">
							{vehicleLogDetails && vehicleLogDetails.vehicle_images.length > 0 ? (
								vehicleLogDetails.vehicle_images.map((imageDetails, index) => (
									<EvidenceImageSelection
										index={index}
										selected={selectedEvidenceImages?.includes(imageDetails.id) ?? false}
										src={imageDetails.url}
										handleEvidenceImageClick={() => handleEvidenceImageClick(imageDetails.id)}
										readOnly={readOnlyPermission}
										lockDetails={lockedEvidenceImages.get(imageDetails.id)}
										key={index}
									/>
								))
							) : (
								<Typography fontSize={13} color="#888">
									No evidence images available
								</Typography>
							)}
						</Box>
					</Box>

					{/* Remarks */}
					<Box className="event-log-details-card">
						<Typography fontWeight={600} mb={1}>
							Remarks
						</Typography>
						<TextField
							fullWidth
							multiline
							rows={4}
							variant="outlined"
							color="primary"
							size="small"
							label="Enter remarks"
							placeholder="Add any additional notes or comments..."
							error={!!errors.remarks}
							helperText={errors.remarks?.message?.toString() ?? ""}
							slotProps={{ input: { readOnly: readOnlyPermission } }}
							{...register("remarks", { onChange: () => clearErrors("remarks") })}
						/>
					</Box>
				</Grid2>

				{/* Right Column */}
				<Grid2 size={{ xs: 12, md: 4 }}>
					<Box className="event-log-details-card full-height flex-column">
						{/* Violation Evidence */}
						<Box
							sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.5rem" }}
						>
							<Typography fontWeight={600} mb={1}>
								Violation Evidence
							</Typography>

							{!readOnlyPermission && (
								<Button
									variant="outlined"
									color="secondary"
									size="small"
									startIcon={<CropIcon />}
									onClick={() => handleOpenCropDialog("violation")}
								>
									Adjust Crop
								</Button>
							)}
						</Box>

						<Box sx={{ border: "1px solid #eee", borderRadius: 2, p: 1, mb: 1, display: "flex" }}>
							{violationImageError ? (
								croppedViolationImage ? (
									<img
										src={croppedViolationImage}
										alt="Cropped Violation Evidence"
										style={{
											width: "100%",
											height: "auto",
											objectFit: "contain",
											borderRadius: 4
										}}
									/>
								) : (
									<Typography fontSize={13} color="#888" align="center">
										No violation image available
									</Typography>
								)
							) : vehicleLogDetails?.violation_image ? (
								<img
									src={vehicleLogDetails.violation_image}
									alt="Violation Evidence"
									style={{
										width: "100%",
										height: "auto",
										objectFit: "contain",
										borderRadius: 4
									}}
									onError={() => setViolationImageError(true)}
								/>
							) : (
								<Typography fontSize={13} color="#888" align="center">
									No violation image available
								</Typography>
							)}
						</Box>

						{/* Vehicle Type */}
						<Typography fontWeight={600} mb={1}>
							Vehicle Type
						</Typography>

						<Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1 }}>
							{vehicleTypesList?.map((type) => {
								const isSelected = Number(vehicleType) === type.id;
								return (
									<Box
										key={type.id}
										className={`event-log-selection-button ${isSelected ? "selected" : ""}`}
										onClick={() => !readOnlyPermission && handleChangeVehicleType(type.id)}
										sx={
											readOnlyPermission
												? {
														cursor: "default",
														"&:hover": {
															borderColor: isSelected ? "var(--color-primary-main)" : "#dddddd",
															backgroundColor: isSelected ? "var(--color-primary-light)" : "transparent"
														}
												  }
												: {}
										}
									>
										<Box className="event-log-selection-button-icon-wrapper">
											{type.icon && <img src={type.icon} alt={type.name} />}
										</Box>

										<Typography classes={{ root: "event-log-selection-button-text" }}>{type.name}</Typography>
									</Box>
								);
							})}
						</Box>

						{/* Violation Type */}
						<Typography fontWeight={600} mt={2} mb={1}>
							Violation Type
						</Typography>

						<Box
							sx={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 1, marginBottom: "0.5rem" }}
						>
							{violationsList?.map((violation) => {
								const isSelected = eventViolations.some((v) => v.violation_id === violation.id);
								return (
									<Box
										key={violation.id}
										className={`event-log-selection-button ${isSelected ? "selected" : ""}`}
										onClick={() =>
											!readOnlyPermission &&
											handleViolationClick({
												id: violation.id,
												violation_id: violation.id,
												name: violation.name,
												lane_id: vehicleLogDetails?.lanes?.[0]?.id ?? 0,
												lane: vehicleLogDetails?.lanes?.[0]?.name ?? "",
												is_speed_related: violation.name.toLowerCase().includes("speed")
											})
										}
										sx={
											readOnlyPermission
												? {
														cursor: "default",
														"&:hover": {
															borderColor: isSelected ? "var(--color-primary-main)" : "#dddddd",
															backgroundColor: isSelected ? "var(--color-primary-light)" : "transparent"
														}
												  }
												: {}
										}
									>
										<Box className="event-log-selection-button-icon-wrapper">
											{/* {violation.icon && <img src={violation.icon} alt={violation.name} />} */}
										</Box>

										<Typography classes={{ root: "event-log-selection-button-text" }}>
											{NON_HSRP_VIOLATION_NAMES.includes(violation.name)
												? "Non HSRP"
												: convertSnakeToTitleCase(violation.name)}
										</Typography>

										{violation.name.toLowerCase().includes("speed") &&
											vehicleLogDetails &&
											(vehicleLogDetails.radar_speed || vehicleLogDetails.speed) && (
												<Typography fontSize={13} color="#888">
													{vehicleLogDetails.radar_speed || vehicleLogDetails.speed}
												</Typography>
											)}
									</Box>
								);
							})}
						</Box>

						{/* Vahan / Sarathi Integrations */}
						<Box className="integrations-wrapper" sx={onNext && onPrevious ? { gridTemplate: "1fr / 1fr 1fr" } : {}}>
							{getVehicleDetailsLoading || getVehicleDetailsFetching ? (
								<>
									<Box className="integration-button disabled">
										<CircularProgress size={20} color="inherit" />
										<Typography variant="body2" fontWeight={500}>
											Fetching Vehicle Data...
										</Typography>
									</Box>

									{/* <Box className="integration-button disabled">
										<CircularProgress size={16} color="inherit" />
										<Typography variant="body2" fontWeight={500}>
											Fetching Sarathi Data...
										</Typography>
									</Box> */}
								</>
							) : !vehicleNumber || !VALIDATION_REGEX.NUMBER_PLATE.test(vehicleNumber) ? (
								<>
									<Tooltip placement="top" title={!vehicleNumber ? "Missing plate number" : "Invalid plate number"}>
										<Box className="integration-button disabled">
											<CarCrashIcon />
											<Typography variant="body2" fontWeight={500}>
												Vehicle Details
											</Typography>
										</Box>
									</Tooltip>

									{/* <Tooltip placement="top" title={!vehicleNumber ? "Missing plate number" : "Invalid plate number"}>
										<Box className="integration-button disabled">
											<PersonSearchIcon />
											<Typography variant="body2" fontWeight={500}>
												Sarathi Data
											</Typography>
										</Box>
									</Tooltip> */}
								</>
							) : (
								<>
									<Box
										className="integration-button vahan"
										onClick={handleOpenVahanDataDialog}
										sx={{ cursor: "pointer" }}
									>
										<CarCrashIcon />
										<Typography variant="body2" fontWeight={500}>
											Vahan Data
										</Typography>
									</Box>

									{/* <Box
										className="integration-button sarathi"
										onClick={handleOpenSarathiDataDialog}
										sx={{ cursor: "pointer" }}
									>
										<PersonSearchIcon />
										<Typography variant="body2" fontWeight={500}>
											Sarathi Data
										</Typography>
									</Box> */}
								</>
							)}

							<Box className="integration-button previous-offences" onClick={handleOpenPreviousOffencesDialog}>
								<HistoryIcon />
								<Typography variant="body2" fontWeight={500}>
									Previous Offences
								</Typography>
							</Box>
						</Box>
					</Box>
				</Grid2>
			</Grid2>

			{/* Footer Buttons */}
			{!readOnlyPermission && vehicleLogDetails?.status === "pending" ? (
				<Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 2 }}>
					<Button variant="outlined" color="inherit" startIcon={<RestartAltIcon />} onClick={handleResetData}>
						Reset
					</Button>

					<LoadingButton
						variant="outlined"
						color="error"
						startIcon={<CancelIcon />}
						onClick={handleSubmit((formData) => handleSaveButtonClick(formData, true))}
						loading={rejectButtonLoading}
						loadingPosition="start"
					>
						Reject
					</LoadingButton>

					{/* <Button variant="outlined" color="primary" startIcon={<PictureAsPdfIcon />}>
						PDF
					</Button> */}

					{!validateButtonError ? (
						<LoadingButton
							disableElevation
							variant="contained"
							color="success"
							startIcon={<CheckCircleIcon />}
							loading={validateButtonLoading}
							onClick={handleSubmit((formData) => handleSaveButtonClick(formData))}
							loadingPosition="start"
						>
							Validate
						</LoadingButton>
					) : (
						<Tooltip arrow placement="top" title={validateButtonError}>
							<span style={{ cursor: "not-allowed" }}>
								<LoadingButton
									disableElevation
									variant="contained"
									color="success"
									startIcon={<CheckCircleIcon />}
									disabled
								>
									Validate
								</LoadingButton>
							</span>
						</Tooltip>
					)}
				</Box>
			) : authState.user.permissions &&
			  authState.user.permissions.violation_logs.edit &&
			  vehicleLogDetails &&
			  vehicleLogDetails?.status === "approved" &&
			  (!vehicleLogDetails?.is_challan_created || vehicleLogDetails?.retry_challan) ? (
				<Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 2 }}>
					<Button
						disableElevation
						variant="contained"
						color="primary"
						startIcon={<ReceiptLongIcon />}
						onClick={openChallanGenerationDialog}
					>
						Generate Challan
					</Button>
				</Box>
			) : null}

			{fullscreenImageProps ? (
				<Box className="fullscreen-evidence-image-section" onClick={handleHideFullscreenImage}>
					<Box className="fullscreen-evidence-image-wrapper" onClick={(event) => event.stopPropagation()}>
						<FullscreenImage {...fullscreenImageProps} />
					</Box>

					<IconButton
						disableRipple
						sx={{ position: "absolute", top: "0", right: "0" }}
						onClick={handleHideFullscreenImage}
					>
						<FullscreenExitIcon sx={{ color: "white" }} fontSize="large" />
					</IconButton>
				</Box>
			) : null}

			<CropDialog
				open={showCropDialog}
				imageSrc={cropDialogSourceImage?.url ?? ""}
				imageDimensions={{
					width: vehicleLogDetails?.image_width ?? 1280,
					height: vehicleLogDetails?.image_height ?? 720
				}}
				initialCoordinates={
					cropType === "plate"
						? (plateImageCrop as [number, number, number, number] | undefined)
						: (violationImageCrop as [number, number, number, number] | undefined)
				}
				onClose={handleCloseCropDialog}
				onCropComplete={handleCropComplete}
			/>

			<VahanDataDialog
				open={showVahanDataDialog}
				vehicleLogDetails={vehicleLogDetails}
				vehicleDetails={vehicleDetails}
				loading={getVehicleDetailsLoading || getVehicleDetailsFetching}
				onClose={handleCloseVahanDataDialog}
				forceRefetchData={forceRefetchVehicleDetails}
			/>

			{/* <SarathiDataDialog
				open={showSarathiDataDialog}
				vehicleLogDetails={vehicleLogDetails}
				onClose={handleCloseSarathiDataDialog}
			/> */}

			<PreviousOffencesDialog
				open={showPreviousOffencesDialog}
				vehicleLogDetails={vehicleLogDetails}
				onClose={handleClosePreviousOffencesDialog}
			/>

			<GenerateChallanDialog
				open={showChallanGenerationDialog}
				vehicleLogDetails={vehicleLogDetails}
				onClose={closeChallanGenerationDialog}
			/>
		</>
	);
};

export default VehicleLogContent;
